import { useCallback, useRef } from 'react';
import { trackCtaView } from './analytics';

// ── CTA-megjelenés mérése ───────────────────────────────────────────────────
//
// MIÉRT: eddig csak a CTA-kattintást mértük. Egy alacsony kattintásszámból így
// nem derült ki, hogy a gomb rossz-e, vagy a látogató el sem jutott odáig.
// A `cta_view` a hányados NEVEZŐJE (lásd docs/cro-audit-cta.md).
//
// A `cta_view` JELENTÉSE (és ezt a tesztek rögzítik):
//   a CTA legalább 50%-a FOLYAMATOSAN legalább 1000 ms-ig a viewportban van,
//   CTA-nként legfeljebb EGYSZER oldalmegtekintésenként.
//
// Amit az IntersectionObserver NEM lát (ismert korlát, dokumentálva):
//   takarás (cookie-banner overlay, sticky sáv), átlátszóság. A „látszott”
//   tehát „a viewport látható részén volt”, nem szemmel igazolt figyelem.
//
// MIÉRT CALLBACK-REF: a terméklap előbb a betöltő állapotot rendereli, a CTA
// később kerül a DOM-ra. Egy `useRef` + `useEffect([cta])` ilyenkor üres
// ref-fel futott le, és többé nem (ez volt az első változat hibája).

export const LATHATO_ARANY = 0.5;   // a CTA felének látszania kell
export const KUSZOB_MS = 1000;      // ennyi ideig – a végiggörgetés nem számít

/**
 * A mérés magja – React nélkül, ezért önállóan tesztelhető.
 * Megfigyeli a `node`-ot, és EGYSZER meghívja az `onMegjelent`-et, ha a
 * láthatósági feltétel teljesül. Visszaad egy bontó függvényt.
 */
export function figyelMegjelenest(node, onMegjelent, {
  arany = LATHATO_ARANY, kuszobMs = KUSZOB_MS, IO = globalThis.IntersectionObserver,
} = {}) {
  if (!node || typeof IO !== 'function') return () => {};
  let idozito = null;
  let kesz = false;
  const io = new IO(([e]) => {
    if (kesz) return;
    if (e.isIntersecting && e.intersectionRatio >= arany) {
      if (idozito) return;                     // már számol – nem indítjuk újra
      idozito = setTimeout(() => {
        kesz = true;
        io.disconnect();
        onMegjelent();
      }, kuszobMs);
    } else if (idozito) {
      clearTimeout(idozito);                   // a küszöb előtt kikerült
      idozito = null;
    }
  }, { threshold: [arany] });
  io.observe(node);
  return () => { if (idozito) clearTimeout(idozito); io.disconnect(); };
}

/**
 * @param {string} cta          stabil CTA-azonosító (TERMEK_CTA)
 * @param {object} ctx          kontextus a deviceParams-hoz (device_id nélkül megy ki)
 * @param {string} resetKulcs   ha változik (pl. a termék uid-je), a mérés
 *                              újraindul – ugyanazon komponens-példányon belüli
 *                              termék→termék váltásra. Csak belső kulcs, NEM megy ki.
 * @returns callback-ref a mérendő elemre
 */
export default function useCtaView(cta, ctx = null, resetKulcs = '') {
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  // MEGTEKINTÉSENKÉNT egyedi token: minden mount újat kap. Ezzel lesz a
  // „termék → lista → ugyanaz a termék” második útja is új megtekintés.
  const tokenRef = useRef(null);
  if (tokenRef.current === null) tokenRef.current = Math.random().toString(36).slice(2);
  const bontRef = useRef(null);
  const mertRef = useRef(null);        // melyik resetKulcsra mértünk már

  return useCallback((node) => {
    if (bontRef.current) { bontRef.current(); bontRef.current = null; }
    if (!node || !cta || mertRef.current === resetKulcs) return;
    bontRef.current = figyelMegjelenest(node, () => {
      mertRef.current = resetKulcs;
      trackCtaView(cta, ctxRef.current, `${tokenRef.current}:${resetKulcs}`);
    });
  }, [cta, resetKulcs]);
}
